import { Button, Callout } from '@dev-spendesk/grapes';
import { useFormik } from 'formik';
import { useQueryClient } from 'react-query';

import { toApiFormat as toApiApprovalRules } from 'modules/company/structure/approval-flows/models';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useCompanyCurrency } from 'src/core/modules/app/hooks/useCompanyCurrency';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import {
  NotificationType,
  useNotifications,
} from 'src/core/modules/app/notifications';
import { createApprovalScheme } from 'src/core/modules/company/cost-centers/containers/CostCenterFormModalContainer/hooks/createApprovalScheme';
import { createApprovalWorkflow } from 'src/core/modules/company/cost-centers/containers/CostCenterFormModalContainer/hooks/createApprovalWorkflow';
import { editApprovalScheme } from 'src/core/modules/company/cost-centers/containers/CostCenterFormModalContainer/hooks/editApprovalSchemeQuery';
import { type ApprovalRule } from 'src/core/modules/company/structure/approval-flows';

import { validateForm } from './validate';
import { defaultApprovalRules } from '../../approvalScheme';
import { APPROVAL_SCHEMES_QUERY_KEY } from '../../hooks/useApprovalSchemesQuery';
import { ApprovalScheme } from '../ApprovalScheme';

type Props = {
  onClose: () => void;
  approvalRules?: ApprovalRule[];
  approvalSchemeId?: string;
};

export type FormProps = {
  approvalRules: ApprovalRule[];
};

export const StandardApprovalFlowPanelContent = ({
  onClose,
  approvalRules,
  approvalSchemeId,
}: Props) => {
  const companyId = useCompanyId();
  const { t } = useTranslation('global');
  const queryClient = useQueryClient();
  const { pushNotif } = useNotifications();
  const currency = useCompanyCurrency();

  const isEditing = approvalSchemeId !== undefined;

  const {
    values: formValues,
    errors,
    setFieldValue,
    handleSubmit,
    isSubmitting,
  } = useFormik<FormProps>({
    enableReinitialize: true,
    initialValues: {
      approvalRules: approvalRules ?? defaultApprovalRules(currency),
    },
    validateOnChange: false,
    validate: (values) => validateForm(values, t),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        if (isEditing) {
          await editApprovalScheme({
            companyId,
            payload: {
              rules: toApiApprovalRules(values.approvalRules),
            },
            approvalSchemeId,
          });
        } else {
          const approvalScheme = await createApprovalScheme(
            {
              owner: null,
              rules: toApiApprovalRules(values.approvalRules),
            },
            companyId,
          );
          await createApprovalWorkflow(
            { approvalSchemeId: approvalScheme.id },
            companyId,
          );
        }
      } catch (error) {
        pushNotif({
          type: NotificationType.Danger,
          message: t('approvalWorkflows.notifications.error'),
        });
        throw new Error(t('approvalWorkflows.notifications.error'), error);
      }
      setSubmitting(false);
      onClose();

      // Invalidate appropriate queries
      queryClient.invalidateQueries(APPROVAL_SCHEMES_QUERY_KEY);
      if (!isEditing) {
        queryClient.invalidateQueries(['approval-workflows', 'active']);
      }

      pushNotif({
        type: NotificationType.Success,
        message: t('approvalWorkflows.notifications.success'),
      });
    },
  });

  return (
    <>
      <div className="grow overflow-y-auto">
        <Callout
          className="m-32"
          title={t('approvalWorkflows.standardApprovalFlow.callout.title')}
          variant="info"
        >
          {t('approvalWorkflows.standardApprovalFlow.callout.description')}
        </Callout>
        <div className="text-content-primary title-m mb-16 px-32">
          {t('approvalWorkflows.standardApprovalFlow.approvalRequiredFrom')}
        </div>
        <div className="p-32 pt-0 pb-[90px]">
          <ApprovalScheme
            error={
              errors.approvalRules
                ? (errors.approvalRules as string)
                : undefined
            }
            rules={formValues.approvalRules}
            onChange={(rules) => {
              setFieldValue('approvalRules', rules);
            }}
          />
        </div>
      </div>
      <div className="rounded-bl-12 rounded-br-12 border-t-border-default bg-background-primary-default absolute bottom-0 z-10 flex w-full justify-between gap-16 border-0 border-t border-solid px-32 py-16">
        <Button
          text={t('approvalWorkflows.standardApprovalFlow.cancel')}
          className="flex-grow"
          variant="secondaryNeutral"
          onClick={onClose}
        />
        <Button
          text={t('approvalWorkflows.standardApprovalFlow.save')}
          className="flex-grow"
          variant="primaryBrand"
          isLoading={isSubmitting}
          isDisabled={isSubmitting}
          onClick={() => handleSubmit()}
        />
      </div>
    </>
  );
};
